import { createWebHashHistory, createRouter } from "vue-router";
import { canAccessRoute, currentUserRole } from "@/roles";

// ===== AUTH =====
import Login from "@/components/Login.vue";
import BreakGlassLogin from "@/components/BreakGlassLogin.vue";
import Register from "@/components/Register.vue";
import ResetPasswordPage from "@/components/ResetPasswordPage.vue";
import Profile from "@/components/hms-auth/Profile.vue";
import ChangePassword from "@/components/hms-auth/ChangePassword.vue";
import Security from "@/components/hms-auth/Security.vue";

// ===== MULTI-TENANT PLATFORM =====
import OrgSignup            from "@/components/OrgSignup.vue";
import OrgSignupStatus      from "@/components/OrgSignupStatus.vue";
import PlatformConsole      from "@/components/PlatformConsole.vue";
import OrgAdminDashboard    from "@/components/OrgAdminDashboard.vue";
import ForceChangePassword  from "@/components/ForceChangePassword.vue";

// ===== DASHBOARD =====
import Dashboard from "@/components/Dashboard.vue";

// ===== LANDING =====
import LandingPage from "@/components/LandingPage.vue";

// ===== PATIENTS =====
import PatientManagement from "@/components/hms-patients/PatientManagement.vue";
import PatientChart      from "@/components/hms-patients/PatientChart.vue";

// ===== DATA GOVERNANCE =====
import DataGovernance    from "@/components/hms-admin/DataGovernance.vue";

// ===== STAFF AVAILABILITY =====
import StaffAvailability from "@/components/hms-hr/StaffAvailability.vue";

// ===== REVENUE BY DEPARTMENT =====
import RevenueByDepartment from "@/components/hms-revenue/RevenueByDepartment.vue";

// ===== BLOOD BANK PUBLIC =====
import BloodStatusPublic from "@/components/hms-public/BloodStatusPublic.vue";

// ===== WORKFLOW & BILLING SETTINGS =====
import WorkflowSettings from "@/components/hms-admin/WorkflowSettings.vue";

// ===== AMBULANCE =====
import AmbulanceManagement from "@/components/hms-ambulance/AmbulanceManagement.vue";

// ===== PUBLIC (no-auth) =====
import PayPublic                 from "@/components/hms-public/PayPublic.vue";
import AmbulanceTrackerPublic    from "@/components/hms-public/AmbulanceTrackerPublic.vue";
import AmbulanceCallerGpsPublic  from "@/components/hms-public/AmbulanceCallerGpsPublic.vue";

// ===== WARDS & BEDS =====
import WardsAndBeds from "@/components/hms-ward/WardsAndBeds.vue";

// ===== PHARMACY =====
import PharmacyManagement from "@/components/hms-pharmacy/PharmacyManagement.vue";

// ===== LAB =====
import LabManagement   from "@/components/hms-lab/LabManagement.vue";
import LabResultsEntry from "@/components/hms-lab/LabResultsEntry.vue";

// ===== BILLING =====
import BillingManagement from "@/components/hms-billing/BillingManagement.vue";

// ===== QUEUE =====
import QueueManagement from "@/components/hms-queue/QueueManagement.vue";
import QueueDisplay    from "@/components/hms-queue/QueueDisplay.vue";

// ===== REGISTRIES =====
import RegistryHub  from "@/components/hms-registry/RegistryHub.vue";
import RegistryView from "@/components/hms-registry/RegistryView.vue";

// ===== THEATRE & SURGERY =====
import TheatreManagement from "@/components/hms-theatre/TheatreManagement.vue";

// ===== APPOINTMENTS =====
import AppointmentsManagement from "@/components/hms-appointments/AppointmentsManagement.vue";

// ===== STAFF / HR =====
import StaffManagement from "@/components/hms-admin/StaffManagement.vue";

// ===== BRANCHES =====
import BranchesManagement from "@/components/hms-admin/BranchesManagement.vue";

// ===== USERS & ROLES =====
import UsersAndRoles from "@/components/hms-admin/UsersAndRoles.vue";

// ===== SETTINGS =====
import SettingsManagement from "@/components/hms-admin/SettingsManagement.vue";

// ===== AUDIT LOG =====
import AuditLogViewer from "@/components/hms-admin/AuditLogViewer.vue";

// ===== SERVICE CATALOG =====
import ServiceCatalog from "@/components/hms-admin/ServiceCatalog.vue";

// ===== EQUIPMENT REGISTER =====
import EquipmentRegister from "@/components/hms-equipment/EquipmentRegister.vue";

// ===== BED CLEANING / HOUSEKEEPING =====
import BedCleaningQueue from "@/components/hms-housekeeping/BedCleaningQueue.vue";

// ===== INSURANCE CLAIMS =====
import InsuranceClaims from "@/components/hms-insurance/InsuranceClaims.vue";

// ===== SHA INTEGRATION =====
import ShaIntegration from "@/components/hms-sha/ShaIntegration.vue";

// ===== SMS OUTBOX =====
import SmsOutbox from "@/components/hms-sms/SmsOutbox.vue";

// ===== PRINT TEMPLATES =====
import PrintDocument from "@/components/hms-print/PrintDocument.vue";

// ===== RADIOLOGY =====
import RadiologyManagement from "@/components/hms-radiology/RadiologyManagement.vue";

// ===== NOTIFICATIONS =====
import NotificationsInbox from "@/components/hms-notifications/NotificationsInbox.vue";

// ===== EMERGENCY DEPARTMENT =====
import EdBoard        from "@/components/hms-ed/EdBoard.vue";
import TriageStation  from "@/components/hms-ed/TriageStation.vue";

// ===== CONSULTATION CONSOLE =====
import ConsultationConsole from "@/components/hms-consultation/ConsultationConsole.vue";

// ===== ADMISSIONS =====
import AdmissionsManagement from "@/components/hms-admissions/AdmissionsManagement.vue";

// ===== REFERRALS =====
import ReferralInbox from "@/components/hms-referrals/ReferralInbox.vue";

// (legacy patient portal at /portal removed — merged into /patient/login)

// ===== REPORTS =====
import ReportsDashboard from "@/components/hms-reports/ReportsDashboard.vue";
import VisitsSummary    from "@/components/hms-reports/VisitsSummary.vue";
import DepartmentOverview from "@/components/hms-reports/DepartmentOverview.vue";

// ===== MORGUE =====
import MorgueManagement from "@/components/hms-morgue/MorgueManagement.vue";

// ===== MEDICATION ADMINISTRATION RECORD =====
import MedicationAdministration from "@/components/hms-mar/MedicationAdministration.vue";

// ===== VITALS & NEWS2 =====
import VitalsCharting from "@/components/hms-vitals/VitalsCharting.vue";

// ===== MATERNITY =====
import MaternityManagement from "@/components/hms-maternity/MaternityManagement.vue";
import MaternalSafety      from "@/components/hms-maternity/MaternalSafety.vue";
import MaternalMortality   from "@/components/hms-maternity/MaternalMortality.vue";
import ChvCompanion        from "@/components/hms-maternity/ChvCompanion.vue";
import NewbornRecord       from "@/components/hms-maternity/NewbornRecord.vue";
import SamRegister         from "@/components/hms-maternity/SamRegister.vue";
import PatientTimeline     from "@/components/hms-maternity/PatientTimeline.vue";
import WomensHealth        from "@/components/hms-maternity/WomensHealth.vue";
import TimelineSearch      from "@/components/hms-maternity/TimelineSearch.vue";

// ===== BLOOD BANK =====
import BloodBank from "@/components/hms-blood/BloodBank.vue";

// ===== IMMUNIZATION REGISTER (KEPI) =====
import ImmunizationRegister from "@/components/hms-immunization/ImmunizationRegister.vue";

// ===== HIV / ART REGISTER =====
import ArtRegister from "@/components/hms-art/ArtRegister.vue";

// ===== TB REGISTER (TIBU) =====
import TbRegister from "@/components/hms-tb/TbRegister.vue";

// ===== NICU / NEONATAL =====
import NicuManagement from "@/components/hms-nicu/NicuManagement.vue";

// ===== GEO REFERENCING =====
import FacilityMap from "@/components/hms-geo/FacilityMap.vue";

// ===== COMMUNITY HEALTH =====
import CommunityHealth from "@/components/hms-community/CommunityHealth.vue";

// ===== EXECUTIVE DASHBOARDS =====
import ExecutiveDashboard from "@/components/hms-exec/ExecutiveDashboard.vue";

// ===== NTUTU CARE =====
import NtutuCare from "@/components/hms-ntutu/NtutuCare.vue";

// ===== WELLNESS / HEALTH TALKS =====
import WellnessContent from "@/components/hms-wellness/WellnessContent.vue";

// ===== SHIFT HANDOVER =====
import ShiftHandover from "@/components/hms-shift/ShiftHandover.vue";

// ===== INSURANCE RATE CARDS =====
import InsuranceRateCards from "@/components/hms-insurance-rates/InsuranceRateCards.vue";

// ===== REVENUE WATERFALL =====
import RevenueWaterfall from "@/components/hms-revenue/RevenueWaterfall.vue";

// ===== MoH STATUTORY REPORTS =====
import MohReports from "@/components/hms-moh/MohReports.vue";

// ===== STORES / PROCUREMENT =====
import StoresManagement from "@/components/hms-stores/StoresManagement.vue";

// ===== HRH (Organogram + Duty Roster) =====
import HrhManagement from "@/components/hms-hrh/HrhManagement.vue";

// ===== DIGITAL HEALTH (DHIS2 / KMHFL / eCHIS) =====
import DigitalHealth from "@/components/hms-digital-health/DigitalHealth.vue";

// ===== ANAESTHESIA RECORD =====
import AnaesthesiaRecord from "@/components/hms-anaesthesia/AnaesthesiaRecord.vue";

// ===== FINANCE CONTROLS (Cashier shifts, Approvals, Aging, IFMIS, Banking) =====
import FinanceControls from "@/components/hms-finance/FinanceControls.vue";

// ===== MORTALITY / M&M REGISTER =====
import MortalityRegister from "@/components/hms-mortality/MortalityRegister.vue";

// ===== FAMILY PLANNING =====
import FamilyPlanning from "@/components/hms-fp/FamilyPlanning.vue";

// ===== MENTAL HEALTH =====
import MentalHealth from "@/components/hms-mental-health/MentalHealth.vue";

// ===== SGBV / GBV-RC =====
import SgbvRegister from "@/components/hms-sgbv/SgbvRegister.vue";

// ===== PATIENT FEEDBACK =====
import PatientFeedback from "@/components/hms-feedback/PatientFeedback.vue";

// ===== CSSD =====
import CssdManagement from "@/components/hms-cssd/CssdManagement.vue";

// ===== SPECIALTY REGISTRIES (DM, Cancer, Dialysis, Nutrition) =====
import SpecialtyRegistries from "@/components/hms-specialty/SpecialtyRegistries.vue";

// ===== HR SELF-SERVICE =====
import HrSelfService from "@/components/hms-hr/HrSelfService.vue";

// ===== TELEHEALTH =====
import Telehealth from "@/components/hms-telehealth/Telehealth.vue";

// ===== HOSPITAL KPI DASHBOARD =====
import HospitalKpi from "@/components/hms-kpi/HospitalKpi.vue";

// ===== e-REFERRALS =====
import Referrals from "@/components/hms-referrals/Referrals.vue";

// ===== NOTIFICATIONS =====
import NotificationsCentre from "@/components/hms-notifications/Notifications.vue";

// ===== CRON / SCHEDULED JOBS =====
import CronJobsAdmin from "@/components/hms-cron/CronJobs.vue";

// ===== PATIENT PORTAL (new OTP-login version) =====
import PatientSelfServiceLogin from "@/components/hms-patient-portal/PatientLogin.vue";
import PatientChangePassword   from "@/components/hms-patient-portal/PatientChangePassword.vue";
import PatientSelfService      from "@/components/hms-patient-portal/PatientPortal.vue";
import PatientLabPrint         from "@/components/hms-patient-portal/PatientLabPrint.vue";
import PatientVisitPrint       from "@/components/hms-patient-portal/PatientVisitPrint.vue";
import EmergencyView           from "@/components/hms-patient-portal/EmergencyView.vue";

// ===== AI ASSISTANT =====
import AIAssistant from "@/components/hms-ai/AIAssistant.vue";

const routes = [
    // ===== PUBLIC ROUTES =====
    { path: "/",               name: "landing",        component: LandingPage,       meta: { public: true  } },
    { path: "/login",          name: "login",          component: Login,             meta: { public: true  } },
    { path: "/break-glass",    name: "break-glass",    component: BreakGlassLogin,   meta: { public: true  } },
    { path: "/register",       name: "register",       component: Register,          meta: { public: true  } },

    // Multi-tenant signup + per-scope home pages
    { path: "/signup",          name: "org-signup",        component: OrgSignup,           meta: { public: true  } },
    { path: "/signup-status",   name: "org-signup-status", component: OrgSignupStatus,     meta: { public: true  } },
    { path: "/platform",        name: "platform-console",  component: PlatformConsole,     meta: { public: false } },
    { path: "/org",             name: "org-admin",         component: OrgAdminDashboard,   meta: { public: false } },
    { path: "/change-password", name: "change-password",   component: ForceChangePassword, meta: { public: false } },
    { path: "/reset-password", name: "reset-password", component: ResetPasswordPage, meta: { public: true  } },

    // ===== DASHBOARD =====
    { path: "/dashboard",      name: "dashboard",      component: Dashboard,         meta: { public: false } },

    // ===== PATIENTS =====
    { path: "/patients",                       name: "patients",      component: PatientManagement, meta: { public: false } },
    { path: "/patients/:patientId/chart",      name: "patient-chart", component: PatientChart,      meta: { public: false } },

    // ===== AMBULANCE =====
    { path: "/ambulance",                      name: "ambulance",     component: AmbulanceManagement, meta: { public: false } },

    // ===== PUBLIC PAGES (no login required, exposed via shareable token) =====
    { path: "/pay/:token",                    name: "pay-public",                  component: PayPublic,                 meta: { public: true } },
    { path: "/ambulance-tracker/:token",      name: "ambulance-tracker-public",    component: AmbulanceTrackerPublic,    meta: { public: true } },
    { path: "/ambulance-caller-gps/:token",   name: "ambulance-caller-gps-public", component: AmbulanceCallerGpsPublic,  meta: { public: true } },

    // ===== WARDS & BEDS =====
    { path: "/wards",          name: "wards",          component: WardsAndBeds,      meta: { public: false } },

    // ===== PHARMACY =====
    { path: "/pharmacy",       name: "pharmacy",       component: PharmacyManagement, meta: { public: false } },

    // ===== LAB =====
    { path: "/lab",            name: "lab",            component: LabManagement,      meta: { public: false } },
    { path: "/lab/results",    name: "lab-results",    component: LabResultsEntry,    meta: { public: false } },

    // ===== BILLING =====
    { path: "/billing",        name: "billing",        component: BillingManagement,  meta: { public: false } },

    // ===== QUEUE (protected staff console) =====
    { path: "/queue",          name: "queue",          component: QueueManagement,    meta: { public: false } },

    // ===== QUEUE DISPLAY (PUBLIC — per bible §11 rule 15, always public, never add auth guard) =====
    { path: "/queue-display",  name: "queue-display",  component: QueueDisplay,       meta: { public: true  } },

    // ===== REGISTRIES =====
    { path: "/registry",        name: "registry-hub",  component: RegistryHub,        meta: { public: false } },
    { path: "/registry/:type",  name: "registry-view", component: RegistryView,       meta: { public: false } },

    // ===== THEATRE & SURGERY =====
    { path: "/theatre",         name: "theatre",       component: TheatreManagement,        meta: { public: false } },

    // ===== APPOINTMENTS =====
    { path: "/appointments",    name: "appointments",  component: AppointmentsManagement,   meta: { public: false } },

    // ===== ADMIN HUB (bare /admin) =====
    // No dedicated admin landing page — send anyone who lands on /admin to Users & Roles
    // (the most common admin action). Same treatment for a trailing-slash variant.
    { path: "/admin",           redirect: "/admin/users" },
    { path: "/admin/",          redirect: "/admin/users" },

    // ===== STAFF / HR =====
    { path: "/admin/staff",     name: "staff",         component: StaffManagement,          meta: { public: false } },

    // ===== BRANCHES =====
    { path: "/admin/branches",  name: "branches",      component: BranchesManagement,       meta: { public: false } },

    // ===== USERS & ROLES =====
    { path: "/admin/users",     name: "users",         component: UsersAndRoles,            meta: { public: false } },

    // ===== SETTINGS =====
    { path: "/settings",        name: "settings",      component: SettingsManagement,       meta: { public: false } },

    // ===== AUDIT LOG =====
    { path: "/admin/audit",     name: "audit",         component: AuditLogViewer,           meta: { public: false } },

    // ===== DATA GOVERNANCE =====
    { path: "/admin/data-governance", name: "data-governance", component: DataGovernance,    meta: { public: false } },

    // ===== STAFF AVAILABILITY =====
    { path: "/staff-availability", name: "staff-availability", component: StaffAvailability,  meta: { public: false } },

    // ===== REVENUE BY DEPARTMENT =====
    { path: "/revenue-by-department", name: "revenue-by-department", component: RevenueByDepartment, meta: { public: false } },

    // ===== BLOOD BANK PUBLIC =====
    { path: "/blood-status", name: "blood-status", component: BloodStatusPublic, meta: { public: true } },

    // ===== WORKFLOW & BILLING SETTINGS =====
    { path: "/admin/workflow-settings", name: "workflow-settings", component: WorkflowSettings, meta: { public: false } },

    // ===== SERVICE CATALOG =====
    { path: "/admin/services",  name: "services",      component: ServiceCatalog,           meta: { public: false } },

    // ===== EQUIPMENT REGISTER =====
    { path: "/equipment",       name: "equipment",     component: EquipmentRegister,        meta: { public: false } },

    // ===== BED CLEANING =====
    { path: "/housekeeping",    name: "housekeeping",  component: BedCleaningQueue,         meta: { public: false } },

    // ===== INSURANCE CLAIMS =====
    { path: "/insurance",       name: "insurance",     component: InsuranceClaims,          meta: { public: false } },

    // ===== SHA INTEGRATION (preauth + verifications + webhook) =====
    { path: "/sha",             name: "sha",           component: ShaIntegration,           meta: { public: false } },

    // ===== SMS OUTBOX =====
    { path: "/sms",             name: "sms",           component: SmsOutbox,                meta: { public: false } },

    // ===== PRINT (invoice / receipt / lab / rx / discharge) =====
    // Marked public to bypass the sessionStorage-based router guard — print pages
    // open in a new tab (sessionStorage is per-tab and empty there). The component
    // still checks localStorage.authToken before loading data.
    { path: "/print/:type/:id", name: "print",         component: PrintDocument,            meta: { public: true  } },

    // ===== RADIOLOGY =====
    { path: "/radiology",       name: "radiology",     component: RadiologyManagement,      meta: { public: false } },

    // ===== NOTIFICATIONS =====
    { path: "/notifications",   name: "notifications", component: NotificationsInbox,       meta: { public: false } },

    // ===== EMERGENCY DEPARTMENT =====
    { path: "/ed-board",        name: "ed-board",      component: EdBoard,                  meta: { public: false } },
    { path: "/triage",          name: "triage",        component: TriageStation,            meta: { public: false } },

    // ===== CONSULTATION CONSOLE =====
    { path: "/consultation",    name: "consultation",  component: ConsultationConsole,      meta: { public: false } },

    // ===== ADMISSIONS =====
    { path: "/admissions",      name: "admissions",    component: AdmissionsManagement,     meta: { public: false } },

    // ===== REFERRALS =====
    { path: "/referrals",       name: "referrals",     component: ReferralInbox,            meta: { public: false } },

    // (legacy /portal route removed — patient portal lives at /patient/login + /patient/dashboard)

    // ===== REPORTS =====
    { path: "/reports",         name: "reports",       component: ReportsDashboard,         meta: { public: false } },
    { path: "/visits-summary",  name: "visits-summary", component: VisitsSummary,           meta: { public: false } },
    { path: "/department-overview", name: "department-overview", component: DepartmentOverview, meta: { public: false } },

    // ===== USER PROFILE & PASSWORD =====
    { path: "/profile",         name: "profile",         component: Profile,         meta: { public: false } },
    { path: "/change-password", name: "change-password", component: ChangePassword,  meta: { public: false } },
    { path: "/security",        name: "security",        component: Security,        meta: { public: false } },

    // ===== MORGUE =====
    { path: "/funeral-home",    name: "funeral-home",  component: MorgueManagement,         meta: { public: false } },
    { path: "/morgue",          redirect: "/funeral-home" },

    // ===== MEDICATION ADMINISTRATION RECORD =====
    { path: "/mar",             name: "mar",           component: MedicationAdministration, meta: { public: false } },

    // ===== VITALS & NEWS2 =====
    { path: "/vitals",          name: "vitals",        component: VitalsCharting,           meta: { public: false } },

    // ===== MATERNITY =====
    { path: "/maternity",       name: "maternity",     component: MaternityManagement,      meta: { public: false } },
    { path: "/maternal-safety", name: "maternal-safety", component: MaternalSafety,         meta: { public: false } },
    { path: "/maternal-mortality", name: "maternal-mortality", component: MaternalMortality, meta: { public: false } },
    { path: "/chv",             name: "chv-companion", component: ChvCompanion,             meta: { public: false } },
    { path: "/newborn/:id",     name: "newborn-record", component: NewbornRecord,           meta: { public: false } },
    { path: "/sam-register",    name: "sam-register",  component: SamRegister,              meta: { public: false } },
    { path: "/patient/:id/timeline", name: "patient-timeline", component: PatientTimeline,   meta: { public: false } },
    { path: "/womens-health",   name: "womens-health", component: WomensHealth,              meta: { public: false } },
    { path: "/timeline",        name: "timeline-search", component: TimelineSearch,          meta: { public: false } },

    // ===== BLOOD BANK =====
    { path: "/blood-bank",      name: "blood-bank",    component: BloodBank,                meta: { public: false } },

    // ===== IMMUNIZATION REGISTER =====
    { path: "/immunization",    name: "immunization",  component: ImmunizationRegister,     meta: { public: false } },

    // ===== HIV / ART REGISTER =====
    { path: "/art",             name: "art",           component: ArtRegister,              meta: { public: false } },

    // ===== TB REGISTER (TIBU) =====
    { path: "/tb",              name: "tb",            component: TbRegister,               meta: { public: false } },

    // ===== NICU / NEONATAL =====
    { path: "/nicu",            name: "nicu",          component: NicuManagement,           meta: { public: false } },

    // ===== GEO REFERENCING =====
    { path: "/geo",             name: "geo",           component: FacilityMap,              meta: { public: false } },

    // ===== COMMUNITY HEALTH =====
    { path: "/community",       name: "community",     component: CommunityHealth,          meta: { public: false } },

    // ===== EXECUTIVE DASHBOARDS =====
    { path: "/exec",            name: "exec",          component: ExecutiveDashboard,       meta: { public: false } },

    // ===== NTUTU CARE =====
    { path: "/ntutu",           name: "ntutu",         component: NtutuCare,                meta: { public: false } },

    // ===== WELLNESS / HEALTH TALKS =====
    { path: "/wellness",        name: "wellness",      component: WellnessContent,          meta: { public: false } },

    // ===== SHIFT HANDOVER =====
    { path: "/shift-handover",  name: "shift-handover", component: ShiftHandover,           meta: { public: false } },

    // ===== INSURANCE RATE CARDS =====
    { path: "/insurance-rates", name: "insurance-rates", component: InsuranceRateCards,     meta: { public: false } },

    // ===== REVENUE WATERFALL =====
    { path: "/revenue-acts",    name: "revenue-acts",  component: RevenueWaterfall,         meta: { public: false } },

    // ===== MoH STATUTORY REPORTS =====
    { path: "/moh-reports",     name: "moh-reports",   component: MohReports,               meta: { public: false } },

    // ===== STORES / PROCUREMENT =====
    { path: "/stores",          name: "stores",        component: StoresManagement,         meta: { public: false } },

    // ===== HRH (Organogram + Duty Roster) =====
    { path: "/hrh",             name: "hrh",           component: HrhManagement,            meta: { public: false } },

    // ===== DIGITAL HEALTH =====
    { path: "/digital-health",  name: "digital-health",component: DigitalHealth,            meta: { public: false } },

    // ===== ANAESTHESIA RECORD =====
    { path: "/anaesthesia",     name: "anaesthesia",   component: AnaesthesiaRecord,        meta: { public: false } },

    // ===== FINANCE CONTROLS =====
    { path: "/finance-controls", name: "finance-controls", component: FinanceControls,      meta: { public: false } },

    // ===== MORTALITY / M&M REGISTER =====
    { path: "/mortality",       name: "mortality",     component: MortalityRegister,        meta: { public: false } },

    // ===== FAMILY PLANNING =====
    { path: "/fp",              name: "fp",            component: FamilyPlanning,           meta: { public: false } },

    // ===== MENTAL HEALTH =====
    { path: "/mental-health",   name: "mental-health", component: MentalHealth,             meta: { public: false } },

    // ===== SGBV / GBV-RC =====
    { path: "/sgbv",            name: "sgbv",          component: SgbvRegister,             meta: { public: false } },

    // ===== PATIENT FEEDBACK =====
    { path: "/feedback",        name: "feedback",      component: PatientFeedback,          meta: { public: false } },

    // ===== CSSD =====
    { path: "/cssd",            name: "cssd",          component: CssdManagement,           meta: { public: false } },

    // ===== SPECIALTY REGISTRIES =====
    { path: "/specialty",       name: "specialty",     component: SpecialtyRegistries,      meta: { public: false } },

    // ===== HR SELF-SERVICE =====
    { path: "/hr-self-service", name: "hr-self-service", component: HrSelfService,          meta: { public: false } },

    // ===== TELEHEALTH =====
    { path: "/telehealth",      name: "telehealth",    component: Telehealth,               meta: { public: false } },

    // ===== HOSPITAL KPI DASHBOARD =====
    { path: "/kpi",             name: "hospital-kpi",  component: HospitalKpi,              meta: { public: false } },

    // ===== e-REFERRALS =====
    { path: "/referrals",       name: "referrals",     component: Referrals,                meta: { public: false } },

    // ===== NOTIFICATIONS =====
    { path: "/notifications",   name: "notifications", component: NotificationsCentre,      meta: { public: false } },

    // ===== CRON / SCHEDULED JOBS =====
    { path: "/cron",            name: "cron-jobs",     component: CronJobsAdmin,            meta: { public: false } },

    // ===== PATIENT PORTAL (separate auth, public routes) =====
    { path: "/portal/login",            name: "portal-login",            component: PatientSelfServiceLogin, meta: { public: true } },
    { path: "/portal/change-password",  name: "portal-change-password",  component: PatientChangePassword,   meta: { public: true } },
    { path: "/portal",                  name: "portal",                  component: PatientSelfService,      meta: { public: true } },
    { path: "/portal/print/lab/:id",   name: "portal-lab-print",   component: PatientLabPrint,   meta: { public: true } },
    { path: "/portal/print/visit/:id", name: "portal-visit-print", component: PatientVisitPrint, meta: { public: true } },
    { path: "/emergency/:token",       name: "emergency-view",     component: EmergencyView,     meta: { public: true } },
    { path: "/patient/login",     redirect: "/portal/login" },
    { path: "/patient/dashboard", redirect: "/portal" },

    // ===== AI ASSISTANT =====
    { path: "/ai",              name: "ai-assistant",  component: AIAssistant,              meta: { public: false } }
];

const router = createRouter({
    history: createWebHashHistory(),
    routes
});

let userActivityTimer;

router.beforeEach((to, from, next) => {
    clearTimeout(userActivityTimer);

    // Patient portal: if must_change_password flag is set, force the change-password
    // screen before any other /portal/* page (except the change-password screen and login itself).
    if (
        to.path.startsWith('/portal') &&
        to.path !== '/portal/login' &&
        to.path !== '/portal/change-password' &&
        localStorage.getItem('patientPortalMustChangePassword') === '1' &&
        localStorage.getItem('patientPortalToken')
    ) {
        next('/portal/change-password');
        return;
    }

    if (!to.meta.public) {
        if (sessionStorage.getItem("isLoggedIn")) {
            // Role-based access control — restricted roles can only see their scope.
            // Admin roles (super_admin / hospital_admin / platform_admin / etc.) pass through.
            // Unknown / not-in-matrix roles fail-open (see src/roles.js design notes).
            var role = currentUserRole();
            if (role && !canAccessRoute(role, to.path)) {
                next({ path: "/dashboard", query: { denied: to.path.replace(/^\//,'') } });
                return;
            }
            next();

            // Inactivity timer — clinical work is slow, so 30 min is a reasonable
            // balance for DHA SEC-AUTH-001 without kicking doctors out mid-visit.
            userActivityTimer = setTimeout(() => {
                sessionStorage.removeItem("isLoggedIn");
                router.push({ path: "/login", query: { timeout: "1" } });
            }, INACTIVITY_MS);
        } else {
            next("/login");
        }
    } else {
        next();
    }
});

// 30 minutes of NO real activity before we log the user out.
var INACTIVITY_MS = 30 * 60 * 1000;

function handleUserActivity() {
    if (!sessionStorage.getItem("isLoggedIn")) return;

    clearTimeout(userActivityTimer);
    userActivityTimer = setTimeout(() => {
        sessionStorage.removeItem("isLoggedIn");
        router.push({ path: "/login", query: { timeout: "1" } });
    }, INACTIVITY_MS);
}

// Listen broadly — mousemove alone misses users who click, scroll, or use touch.
// { passive: true } avoids blocking scroll perf on mobile.
var activityEvents = ["mousemove", "mousedown", "click", "keydown", "scroll", "touchstart", "wheel"];
activityEvents.forEach(function (evt) {
    document.addEventListener(evt, handleUserActivity, { passive: true });
});

export default router;
